import { fn, get } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import findBy from '@nullvoxpopuli/ember-composable-helpers/helpers/find-by';
import type AbilitiesService from 'ember-can/services/abilities';
import truncate from 'ember-cli-string-helpers/helpers/truncate';
import sub from 'ember-math-helpers/helpers/sub';
import { and, eq, gt, lt, lte, or } from 'ember-truth-helpers';
import type { AnyFlow } from 'garaje/components/sign-in-fields/rules-modal';
import MessageBox from 'garaje/components/ui/classic/message-box';
import UiButton from 'garaje/components/ui-button';
import FeatureEnabled from 'garaje/helpers/feature-enabled';
import type SignInFieldActionModel from 'garaje/models/sign-in-field-action';
import type SignInFieldActionRuleGroupModel from 'garaje/models/sign-in-field-action-rule-group';

interface RulesListSignature {
  Args: {
    handleDelete: (rule: SignInFieldActionRuleGroupModel) => unknown;
    handleEdit: (rule: SignInFieldActionRuleGroupModel) => unknown;
    flow: AnyFlow;
    isGlobalChild?: boolean;
    isLocationAdmin?: boolean;
    isProtect?: boolean;
    rules: SignInFieldActionRuleGroupModel[];
    handleAdd: (event: PointerEvent) => void;
  };
}

function getOptionsForCondition(condition: SignInFieldActionModel) {
  return condition.signInField.content?.options ?? [];
}

export default class RulesList extends Component<RulesListSignature> {
  @service declare abilities: AbilitiesService;

  get canAddRule(): boolean {
    // cannot add rules if this flow is a child of a global flow
    if (this.args.isGlobalChild) return false;

    // For Protect/employee flows
    if (this.args.isProtect) {
      // New: If user has employee flow edit permission (when feature flag is on)
      if (this.abilities.can('edit employee registration flow for workplace')) {
        return true;
      }
      // Legacy: When flag is off, allow Global Admins but not Location Admins
      if (this.args.isLocationAdmin) return false;
      return this.abilities.can('edit visitor-type', this.args.flow);
    }

    // For visitor flows, check visitor flow edit permission
    return this.abilities.can('edit visitor-type', this.args.flow);
  }

  get canDeleteRule(): boolean {
    // cannot delete rules if this flow is a child of a global flow
    if (this.args.isGlobalChild) return false;

    // For Protect/employee flows
    if (this.args.isProtect) {
      // New: If user has employee flow edit permission (when feature flag is on)
      if (this.abilities.can('edit employee registration flow for workplace')) {
        return true;
      }
      // Legacy: When flag is off, allow Global Admins but not Location Admins
      if (this.args.isLocationAdmin) return false;
      return this.abilities.can('edit visitor-type', this.args.flow);
    }

    // For visitor flows, check visitor flow edit permission
    return this.abilities.can('edit visitor-type', this.args.flow);
  }

  get canEditRule(): boolean {
    // cannot edit rules if this flow is a child of a global flow
    if (this.args.isGlobalChild) return false;

    // For Protect/employee flows
    if (this.args.isProtect) {
      // New: If user has employee flow edit permission (when feature flag is on)
      if (this.abilities.can('edit employee registration flow for workplace')) {
        return true;
      }
      // Legacy: When flag is off, allow Global Admins but not Location Admins
      // Note: isLocationAdmin prop is only true for location-admin-only users
      // (template passes: and this.isLocationAdmin (not this.isGlobalAdmin))
      if (this.args.isLocationAdmin) return false;
      return this.abilities.can('edit visitor-type', this.args.flow);
    }

    // For visitor flows, check visitor flow edit permission
    return this.abilities.can('edit visitor-type', this.args.flow);
  }

  <template>
    {{#if @rules.length}}
      <div class="flex flex-col items-left -mt-4" data-test-rules-modal-list>
        <div class="mt-4">
          <ul class="p-0 m-0 list-none">
            {{#each @rules as |rule|}}
              <li class="py-6 rule-row">
                <div class="flex justify-between items-center">
                  <div class="font-bold flex-1">
                    <span class="font-normal">When</span>
                    {{#each rule.signInFieldActions as |condition idx|}}
                      {{truncate condition.signInField.content.label 100 true}}<span class="font-normal">
                        {{condition.matchOperator}}
                      </span>{{truncate
                        (get (findBy "id" condition.signInFieldValue (getOptionsForCondition condition)) "value")
                        100
                        true
                      }}
                      <span class="font-normal">
                        {{if (lt idx (sub rule.signInFieldActions.content.length 1)) rule.groupOperator}}
                      </span>
                    {{/each}}
                    <span class="font-normal" style="margin-left: -2.5px;">,</span>
                    {{#each rule.actions as |defined idx|}}
                      <span class="font-normal">
                        {{if (and (gt idx 0) (lte idx (sub rule.actions.length 1))) "and"}}</span>
                      <span class="font-normal">
                        {{#if (eq defined "deny_entry")}}
                          <span>deny entry</span>
                        {{else if (eq defined "alert")}}
                          <span>notify</span>
                          {{#each rule.signInFieldActionsContacts as |contact idx|}}
                            <span class="font-bold" data-test-rule-contact-name>{{get contact.employee "name"}}</span>
                            <span>{{if (lt idx (sub rule.signInFieldActionsContacts.content.length 1)) ","}}</span>
                          {{/each}}
                        {{else}}
                          <span>require entry approval</span>
                        {{/if}}
                      </span>
                    {{/each}}
                    <span class="font-normal" style="margin-left: -2.5px;">.</span>
                  </div>

                  {{#if (or this.canEditRule this.canDeleteRule)}}
                    <div class="rule-action-items ml-4">
                      {{#if this.canEditRule}}
                        <UiButton
                          class="btn btn--smaller btn--square btn--nonHoverFill ml-1"
                          {{on "click" (fn @handleEdit rule)}}
                          data-test-rules-modal-edit-rule
                        >
                          <span class="icon-pencil-dark-full"></span>
                        </UiButton>
                      {{/if}}
                      {{#if this.canDeleteRule}}
                        <UiButton
                          data-test-rules-modal-delete-rule
                          class="btn btn--smaller btn--square btn--nonHoverFill ml-1"
                          {{on "click" (fn @handleDelete rule)}}
                        >
                          <span class="icon-trash"></span>
                        </UiButton>
                      {{/if}}
                    </div>
                  {{/if}}
                </div>

                {{#if (and rule.ignore (FeatureEnabled "ignore-rules"))}}
                  <MessageBox
                    @type="warning"
                    data-test-rule-condition-changed
                    class="mt-4"
                    style="background-position-y: 50%;"
                  >
                    <div class="flex items-center">
                      <span class="flex-1">A condition of this rule has changed. It is inactive until adjusted.</span>
                      {{#if this.canEditRule}}
                        <UiButton
                          class="btn btn--outline btn--small"
                          {{on "click" (fn @handleEdit rule)}}
                          data-test-rule-condition-adjust
                        >
                          Adjust rule
                        </UiButton>
                      {{/if}}
                    </div>
                  </MessageBox>
                {{/if}}
              </li>
            {{/each}}
          </ul>
        </div>
      </div>
    {{else}}
      <div class="flex flex-col items-center m-10" data-test-rules-modal-empty>
        <h4 class="font-normal">Make your sign-in flow smarter with rules.</h4>
        <h4 class="font-normal mt-1">Trigger actions when conditions are met.</h4>
        <img alt="" src="/assets/images/visitors/rules.svg" class="mt-8 h-40" role="presentation" />
      </div>
    {{/if}}

    <div class="flex justify-end mt-8">
      {{#if this.canAddRule}}
        <UiButton class="btn btn--brand btn--small" {{on "click" @handleAdd}} data-test-rules-modal-cta>
          Add
          {{if @rules.length "another"}}
          rule
        </UiButton>
      {{/if}}
    </div>
  </template>
}
